import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Send } from 'lucide-react';

/**
 * Quick Shout Modal for sending broadcast messages to all project participants
 * Appears as a floating action button during active projects
 */
const QuickShoutModal = ({ isOpen, onClose, onSendShout, projectId }) => {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const quickMessages = [
    { emoji: '👋', text: 'On my way!' },
    { emoji: '✨', text: 'Found something!' },
    { emoji: '🆘', text: 'Need help here' },
    { emoji: '🎉', text: 'Great job team!' },
    { emoji: '📍', text: 'Check this location' },
    { emoji: '👀', text: 'Look at this!' },
  ];

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      const t = setTimeout(() => {
        setIsMounted(false);
        setCustomMessage('');
      }, 200);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const handleSendShout = async (message) => {
    if (!message.trim() || isSending) return;

    setIsSending(true);
    try {
      await onSendShout?.(message.trim());
      setCustomMessage('');
      onClose?.();
    } catch (error) {
      console.error('Failed to send shout:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickMessage = (text) => {
    handleSendShout(text);
  };

  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (customMessage.trim().length > 0 && customMessage.trim().length <= 60) {
      handleSendShout(customMessage);
    }
  };

  if (!isMounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[50001]" role="dialog" aria-modal="true" aria-labelledby="quick-shout-modal-title">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black transition-opacity duration-200 ease-out ${isVisible ? 'bg-opacity-50 opacity-100' : 'bg-opacity-0 opacity-0'}`} 
        onClick={onClose}
      />
      
      {/* Modal panel - matches ParticipantsModal structure */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 pointer-events-none">
        <div className={`relative flex flex-col w-full h-full sm:h-auto sm:max-w-md sm:w-full overflow-hidden bg-white dark:bg-gray-800 shadow-xl transition-all duration-200 ease-out sm:rounded-lg transform pointer-events-auto ${isVisible ? 'translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-95 opacity-0'}`}>
          
          {/* Header - clean style matching ParticipantsModal */}
          <div className="sticky top-0 z-10 border-b border-gray-200 dark:border-gray-600/50 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-2">
                <h3 id="quick-shout-modal-title" className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  Quick Shout
                </h3>
              </div>
              <button
                onClick={onClose}
                className="rounded-md p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Subtitle */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Send a message to all participants in this project
            </p>
            
            {/* Quick Messages */}
            <div>
              <div className="grid grid-cols-2 gap-2">
                {quickMessages.map((msg, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickMessage(msg.text)}
                    disabled={isSending}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                  >
                    <span className="text-xl">{msg.emoji}</span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                      {msg.text}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-600/50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  or custom message
                </span>
              </div>
            </div>

            {/* Custom Message */}
            <form onSubmit={handleCustomSubmit}>
              <div>
                <div className="relative">
                  <input
                    type="text"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value.slice(0, 60))}
                    placeholder="Type your message..."
                    className="w-full px-4 py-3 pr-16 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                    maxLength={60}
                    disabled={isSending}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">
                    {customMessage.length}/60
                  </span>
                </div>
              </div>
            </form>
          </div>

          {/* Footer - sticky with safe area inset */}
          <div className="sticky bottom-0 border-t border-gray-200 dark:border-gray-600/50 bg-white dark:bg-gray-800 px-4 py-3 flex flex-col sm:flex-row gap-3 min-h-[56px]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
            <button
              type="button"
              onClick={handleCustomSubmit}
              disabled={!customMessage.trim() || customMessage.trim().length > 60 || isSending}
              className="w-full sm:w-auto sm:flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-sm hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Send className="w-4 h-4" />
              <span>{isSending ? 'Sending...' : 'Send Shout'}</span>
            </button>
            <button
              onClick={onClose}
              className="w-full sm:w-auto sm:flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-xl text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default QuickShoutModal;
